import { type BuildQuizListItem } from '../form/schema/BuildQuizListSchema';

/** 편집 UI 가 삭제 버튼을 감추는 하한과 같다(EditableSubjective·EditableMultiple). */
const MIN_SUBJECTIVE_ANSWER_COUNT = 1;
const MIN_MULTIPLE_CHOICE_COUNT = 2;

type SubjectiveAnswer =
  BuildQuizListItem['subjectiveData']['answerList'][number];
type MultipleChoice = BuildQuizListItem['multipleData']['choiceList'][number];

export type EmptyAnswerInputCleanup =
  | { quizIndex: number; type: 'subjective'; answerList: SubjectiveAnswer[] }
  | { quizIndex: number; type: 'multiple'; choiceList: MultipleChoice[] };

// 빈 칸 판정은 제출 스키마(QuizSubjectiveDataSchema·QuizMultipleDataSchema)가 막는 조건과 같다.
const isEmptyAnswer = (answer: SubjectiveAnswer) => !answer.text;
const isEmptyChoice = (choice: MultipleChoice) =>
  !choice.text && !choice.imageUrl;

const getSubjectiveCleanup = (
  quiz: BuildQuizListItem,
  quizIndex: number,
): EmptyAnswerInputCleanup | undefined => {
  const { answerList } = quiz.subjectiveData;
  const filled = answerList.filter(answer => !isEmptyAnswer(answer));

  if (filled.length === answerList.length) return;
  if (filled.length < MIN_SUBJECTIVE_ANSWER_COUNT) return;

  return { quizIndex, type: 'subjective', answerList: filled };
};

const getMultipleCleanup = (
  quiz: BuildQuizListItem,
  quizIndex: number,
): EmptyAnswerInputCleanup | undefined => {
  const { choiceList } = quiz.multipleData;

  // 정답으로 고른 보기가 비어 있으면 지우지 않고 검증에서 막아 직접 채우게 한다.
  if (choiceList.some(choice => choice.isAnswer && isEmptyChoice(choice))) {
    return;
  }

  const filled = choiceList.filter(choice => !isEmptyChoice(choice));

  if (filled.length === choiceList.length) return;
  if (filled.length < MIN_MULTIPLE_CHOICE_COUNT) return;

  return { quizIndex, type: 'multiple', choiceList: filled };
};

/**
 * 설정 탭으로 넘어가기 전에 지울 단답형 답·선택형 보기 빈 칸을 문제별로 계산한다.
 * 지우면 최소 개수보다 적어지는 문제는 그대로 둬 기존처럼 검증에서 막는다.
 */
export const getEmptyAnswerInputCleanups = (
  quizList: BuildQuizListItem[],
): EmptyAnswerInputCleanup[] =>
  quizList.flatMap((quiz, quizIndex) => {
    const cleanup =
      quiz.type === 'subjective'
        ? getSubjectiveCleanup(quiz, quizIndex)
        : quiz.type === 'multiple'
        ? getMultipleCleanup(quiz, quizIndex)
        : undefined;

    return cleanup ? [cleanup] : [];
  });
